(() => {
  const HEADING_TEXT = 'unsere bestseller von a bis z';
  const collator = new Intl.Collator('de-DE', {
    sensitivity: 'base',
    numeric: true,
    ignorePunctuation: true
  });

  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('de-DE');

  const findSection = () => {
    const headings = [...document.querySelectorAll('.lg-split-heading h2')];
    const heading = headings.find((node) => normalize(node.textContent) === HEADING_TEXT);
    if (!heading) return null;

    const headingWrap = heading.closest('.lg-split-heading');
    if (!headingWrap) return null;

    let row = headingWrap.nextElementSibling;
    while (row && !row.classList.contains('lg-product-row')) row = row.nextElementSibling;
    if (!row) return null;

    return { headingWrap, row };
  };

  const getCollectionHandle = (headingWrap, row) => {
    const link = headingWrap.querySelector('a[href*="/collections/"]')
      || row.parentElement?.querySelector('.lg-mobile-button-wrap a[href*="/collections/"]');
    if (!link) return null;

    try {
      const url = new URL(link.href, window.location.origin);
      const match = url.pathname.match(/\/collections\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_) {
      return null;
    }
  };

  const stripHtml = (html) => {
    const container = document.createElement('div');
    container.innerHTML = html || '';
    return (container.textContent || '').replace(/\s+/g, ' ').trim();
  };

  const truncate = (text, maxLength = 96) => {
    if (!text || text.length <= maxLength) return text || '';
    return `${text.slice(0, maxLength).replace(/\s+\S*$/, '')}…`;
  };

  const getImage = (product) => {
    const image = product.images?.[0] || product.image;
    if (typeof image === 'string') return { src: image, alt: product.title };
    if (image?.src) return { src: image.src, alt: image.alt || product.title };
    return null;
  };

  const getPrice = (product) => {
    const prices = (product.variants || [])
      .map((variant) => Number.parseFloat(variant.price))
      .filter(Number.isFinite);
    if (prices.length) return Math.min(...prices);
    const fallback = Number.parseFloat(product.price);
    return Number.isFinite(fallback) ? fallback : null;
  };

  const formatPrice = (value) => {
    if (value === null) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR'
      }).format(value);
    } catch (_) {
      return `${value.toFixed(2).replace('.', ',')} €`;
    }
  };

  const productUrl = (handle) => {
    const root = window.Shopify?.routes?.root || window.theme?.routes?.home || '/';
    return `${root.replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}`;
  };

  const buildCard = (template, product, fallbackDescription) => {
    const card = template.cloneNode(true);
    card.href = productUrl(product.handle);

    const title = card.querySelector('h3');
    if (title) title.textContent = product.title;

    const description = card.querySelector('.lg-product-card__desc');
    if (description) {
      description.textContent = truncate(stripHtml(product.body_html || product.description || '')) || fallbackDescription;
    }

    const price = card.querySelector('.lg-product-card__price');
    if (price) price.textContent = formatPrice(getPrice(product));

    const imageElement = card.querySelector('.lg-product-card__image img');
    const image = getImage(product);
    if (imageElement && image) {
      imageElement.removeAttribute('srcset');
      imageElement.removeAttribute('sizes');
      imageElement.src = image.src;
      imageElement.alt = image.alt || product.title;
      imageElement.loading = 'lazy';
    } else if (imageElement) {
      imageElement.remove();
    }

    return card;
  };

  const sortExistingCards = (row) => {
    const cards = [...row.querySelectorAll(':scope > .lg-product-card')];
    cards.sort((a, b) => collator.compare(
      a.querySelector('h3')?.textContent?.trim() || '',
      b.querySelector('h3')?.textContent?.trim() || ''
    ));
    cards.forEach((card) => row.appendChild(card));
    row.scrollLeft = 0;
  };

  const loadAllProducts = async (headingWrap, row) => {
    const handle = getCollectionHandle(headingWrap, row);
    const template = row.querySelector(':scope > .lg-product-card');
    if (!handle || !template) {
      sortExistingCards(row);
      return;
    }

    try {
      const response = await fetch(
        `/collections/${encodeURIComponent(handle)}/products.json?limit=250&sort_by=title-ascending`,
        { credentials: 'same-origin', headers: { Accept: 'application/json' } }
      );
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);

      const payload = await response.json();
      const products = [...(payload.products || [])]
        .filter((product) => product?.handle && product?.title)
        .sort((a, b) => collator.compare(a.title, b.title));
      if (!products.length) throw new Error('No products returned');

      const fallbackDescription = template.querySelector('.lg-product-card__desc')?.textContent?.trim()
        || 'Ein besonderer Genussmoment aus unserer Auswahl.';
      const fragment = document.createDocumentFragment();
      products.forEach((product) => fragment.appendChild(buildCard(template, product, fallbackDescription)));

      row.replaceChildren(fragment);
      row.dataset.lgProductCount = String(products.length);
      row.scrollLeft = 0;
    } catch (_) {
      sortExistingCards(row);
    }
  };

  const addControls = (headingWrap, row) => {
    if (headingWrap.querySelector('.lg-product-carousel__controls')) return;

    const controls = document.createElement('div');
    controls.className = 'lg-product-carousel__controls';
    controls.setAttribute('aria-label', 'Produkte alphabetisch durchblättern');

    const previous = document.createElement('button');
    previous.type = 'button';
    previous.className = 'lg-product-carousel__arrow';
    previous.setAttribute('aria-label', 'Vorherige Produkte');
    previous.innerHTML = '<span aria-hidden="true">←</span>';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'lg-product-carousel__arrow';
    next.setAttribute('aria-label', 'Weitere Produkte');
    next.innerHTML = '<span aria-hidden="true">→</span>';

    const update = () => {
      const maxScroll = Math.max(0, row.scrollWidth - row.clientWidth - 2);
      previous.disabled = row.scrollLeft <= 2;
      next.disabled = row.scrollLeft >= maxScroll;
    };

    previous.addEventListener('click', () => row.scrollBy({ left: -row.clientWidth, behavior: 'smooth' }));
    next.addEventListener('click', () => row.scrollBy({ left: row.clientWidth, behavior: 'smooth' }));
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });

    controls.append(previous, next);
    headingWrap.appendChild(controls);
    requestAnimationFrame(update);
  };

  const initialize = async () => {
    const section = findSection();
    if (!section || section.row.dataset.lgCarouselReady === 'true') return;

    section.row.dataset.lgCarouselReady = 'true';
    section.row.classList.add('lg-product-row--carousel');
    section.headingWrap.classList.add('lg-split-heading--carousel');

    await loadAllProducts(section.headingWrap, section.row);
    addControls(section.headingWrap, section.row);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();